import { createApp, defineAsyncComponent } from 'vue';
import App from './App.vue';
import axios from 'axios';
import { createAuth } from '@websanova/vue-auth';
import driverHttpAxios from '@websanova/vue-auth/dist/drivers/http/axios.1.x.esm.js';
import driverDevise from '@websanova/vue-auth/dist/drivers/auth/devise.esm.js';
import driverRouterVueRouter from '@websanova/vue-auth/dist/drivers/router/vue-router.2.x.esm.js';
import VueClipboard from './utils/clipboard';
import { createGtm } from '@gtm-support/vue-gtm';
import { createHead, VueHeadMixin } from '@unhead/vue';
import { InferSeoMetaPlugin } from '@unhead/addons';
import { CapoPlugin } from 'unhead';
import * as Sentry from '@sentry/vue';
import './index.css';

import VueCookies from 'vue-cookies';
import Cable from './utils/cable';
import router from './router/index';
import { installStaleChunkReload } from './utils/stale_chunks';
import i18n, { setLocaleForUser } from './i18n';
import { store } from './store';
import config from './config';
import { USER_CACHE_KEY, writeUserCache, dropUserCache } from './utils/userCache';
import { identifyUser } from './utils/analytics';
import { installViewHeader } from './utils/view_header';
import Logo from './utils/logo';
import {
  clearOnUnauthorized, clearSignedOutUser, enforceActiveUser, isImpersonating,
} from './utils/session';
import {
  filterSentryTransaction,
  ignoredTraceSpanPatterns,
  ignoredTraceTransactionPatterns,
  shouldCreateTraceSpanForRequest,
} from './sentryTracing';

axios.defaults.baseURL = config.uri;

axios.defaults.headers.common['X-GAME'] = store.getters.selectedGame;
axios.defaults.headers.common['X-TEAM-ID'] = localStorage.activeTeamId;

// Ahead of createAuth on purpose: this has to see the token before vue-auth's
// own interceptor removes it (see clearOnUnauthorized).
axios.interceptors.response.use((response) => response, clearOnUnauthorized);
installViewHeader(axios, router);
installStaleChunkReload(router);

const auth = createAuth({
  plugins: {
    router,
    http: axios,
  },
  drivers: {
    auth: driverDevise,
    router: driverRouterVueRouter,
    http: driverHttpAxios,
  },
  options: {
    rolesKey: 'type',
    notFoundRedirect: { name: 'Dashboard' },
    loginData: {
      url: 'auth/sign_in',
      method: 'POST',
      redirect: '/',
    },
    registerData: {
      url: 'auth.json',
      method: 'POST',
      redirect: '/',
    },
    logoutData: {
      url: 'auth/sign_out.json',
      method: 'DELETE',
      redirect: '/login',
    },
    refreshData: {
      enabled: false,
    },
    fetchData: {
      url: 'me',
    },
    // Persist every successful /me so the next boot can hydrate from cache.
    // Fires on login, refresh, impersonate — anything that routes through fetch.
    parseUserData(data) {
      const user = (data && data.data) || {};
      if (Object.keys(user).length) {
        // A different user means the tab is full of someone else's state: clear
        // it and reload before anything renders from it.
        enforceActiveUser(user);
        // The dashboard follows the account's language setting (Settings > Server language).
        setLocaleForUser(user);
        // No-op if a sign-out has latched writes off (in-flight /me race).
        writeUserCache(user);
        // Every sign-in path ends here (email, register, Steam, a returning session), so this
        // is where PostHog learns who the visitor is. Not while an admin is someone else.
        if (!isImpersonating()) {
          identifyUser(user);
        }
      } else {
        // Empty /me — drop any prior snapshot so a later boot can't hydrate it.
        dropUserCache();
      }
      return user;
    },
  },
});

// Pre-hydrate auth state from the cached /me so vue-auth's router guard takes
// its non-blocking branch (authenticated !== null) and the layout renders
// immediately. Only trust the cache when a token is actually present; otherwise
// drop it so a logged-out session can never render stale data. The background
// revalidation is fired after mount (see below).
let hydratedFromCache = false;
if (auth.token()) {
  const cached = localStorage.getItem(USER_CACHE_KEY);
  if (cached) {
    try {
      auth.user(JSON.parse(cached));
      setLocaleForUser(auth.user());
      hydratedFromCache = true;
    } catch (e) {
      dropUserCache();
    }
  }
} else {
  // vue-auth's automatic sign-outs drop the token and redirect but clear nothing
  // else, so a boot with no token is where what they left behind is noticed.
  // Nothing is in flight yet, so writes stay enabled for the sign-in that follows.
  clearSignedOutUser({ latch: false });
}

const head = createHead({
  plugins: [
    InferSeoMetaPlugin(),
    CapoPlugin(),
  ],
});
const app = createApp(App)
  .use(head)
  .use(auth)
  .use(router)
  .use(store)
  .use(i18n)
  .use(Cable, { config, auth })
  .use(VueCookies)
  .use(VueClipboard)
  .use(
    createGtm({
      id: 'GTM-N8ZNXLCT', // Your GTM single container ID, array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy'] or array of objects [{id: 'GTM-xxxxxx', queryParams: { gtm_auth: 'abc123', gtm_preview: 'env-4', gtm_cookies_win: 'x'}}, {id: 'GTM-yyyyyy', queryParams: {gtm_auth: 'abc234', gtm_preview: 'env-5', gtm_cookies_win: 'x'}}], // Your GTM single container ID or array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy']
      loadScript: true, // Whether or not to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
      vueRouter: router, // Pass the router instance to automatically sync with router (optional)
      enabled: true,
    }),
  );

app.config.globalProperties.$logo = Logo;

Sentry.init({
  app,
  dsn: 'https://90122df0fbacbe4fac88164902fb84fc@o4508818021875712.ingest.us.sentry.io/4510458878164992',
  integrations: [
    Sentry.browserTracingIntegration({
      router,
      shouldCreateSpanForRequest: shouldCreateTraceSpanForRequest,
    }),
    Sentry.browserProfilingIntegration(),
    Sentry.vueIntegration({
      tracingOptions: {
        trackComponents: false,
      },
    }),
  ],
  ignoreTransactions: ignoredTraceTransactionPatterns,
  ignoreSpans: ignoredTraceSpanPatterns,
  tracesSampleRate: 0.01,
  beforeSendTransaction: filterSentryTransaction,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  profileSessionSampleRate: 0.1,
  profileLifecycle: 'trace',
  tracePropagationTargets: ['localhost', /^https:\/\/api-k8s\.refrag\.gg\/api/, /^https:\/\/api\.refrag\.gg\/api/],
  sendDefaultPii: true,
});

import('@sentry-internal/replay')
  .then(({ replayIntegration }) => Sentry.addIntegration(replayIntegration()))
  .catch(() => {});

// eslint-disable-next-line vue/multi-word-component-names
app.component('flag', defineAsyncComponent(async () => {
  const [{ Flag }] = await Promise.all([
    import('vue-flag-icon/components'),
    import('vue-flag-icon'),
  ]);
  return Flag;
}));
app.mixin(VueHeadMixin);
app.mount('#app');

// When we rendered from cache, the router guard skipped its usual /me fetch, so
// revalidate in the background. vue-auth's 401 interceptor clears state and
// redirects on a revoked/expired token, so we only need to swallow the reject.
if (hydratedFromCache) {
  auth.fetch().catch(() => {});
}
